import { useState } from 'react';
import { salvarSimplesApuracaoSchema, type SimplesApuracaoListaResponse } from '@triade/contracts';
import { useSalvarSimplesApuracao, useSimplesApuracao } from '@/api/simples-apuracao';
import { ErroFormacao } from '@/components/formacao/avisos-formacao';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { decimalDigitado, decimalParaTextoCampo, formatarDinheiro, formatarFracaoPercentual, formatarHora, formatarMesAno } from '@/lib/format';

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const CAMPO = 'h-9 w-full rounded-md border border-input bg-card px-3 text-body text-ink outline-none focus-visible:border-primary focus-visible:shadow-focus disabled:cursor-not-allowed disabled:opacity-50';

/** "2026-11" ou "2026-11-01" como "Novembro de 2026" (so reordena o calendario; nenhuma conta). */
function rotuloMes(competencia: string): string {
  const [ano = '0', mes = '0'] = competencia.split('-');
  return formatarMesAno(Number(ano), Number(mes));
}

/** Competencia da API ("2026-11-01") como valor de input type=month ("2026-11"). */
const mesDe = (competencia: string): string => competencia.slice(0, 7);

/**
 * Cartao "Simples Nacional: receita e folha de 12 meses" (aba Parametros da Empresa, so com custo.ver). Digita o RBT12 e a folha
 * por competencia; a efetiva calculada da tabela usa esses valores. Dinheiro vai como texto decimal; a tela nao calcula nada.
 */
export function SimplesApuracaoEmpresa({ editavel }: { editavel: boolean }) {
  const lista = useSimplesApuracao();
  if (lista.isError) return <ErroFormacao erro={lista.error} onTentar={() => void lista.refetch()} />;
  if (!lista.data) return <p role="status" className="rounded-2xl border border-dashed border-border bg-card px-5 py-6 text-center text-small text-ink-muted">Carregando apuração do Simples…</p>;
  return <Cartao lista={lista.data} editavel={editavel} />;
}

function Cartao({ lista, editavel }: { lista: SimplesApuracaoListaResponse; editavel: boolean }) {
  const [mes, setMes] = useState(() => mesDe(lista.competenciaAtual));
  const salvo = (m: string) => lista.itens.find((i) => mesDe(i.competencia) === m);
  const [rbt12, setRbt12] = useState(() => decimalParaTextoCampo(salvo(mesDe(lista.competenciaAtual))?.rbt12));
  const [folha12, setFolha12] = useState(() => decimalParaTextoCampo(salvo(mesDe(lista.competenciaAtual))?.folha12));
  const [validacao, setValidacao] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const salvar = useSalvarSimplesApuracao();

  /** Trocar o mes carrega o que ja foi digitado para ele (ou campos vazios). */
  function escolherMes(novo: string) {
    if (novo === '') return;
    setMes(novo);
    setRbt12(decimalParaTextoCampo(salvo(novo)?.rbt12));
    setFolha12(decimalParaTextoCampo(salvo(novo)?.folha12));
    setValidacao(null);
    setErro(null);
    setSalvoEm(null);
  }

  async function gravar() {
    setValidacao(null);
    setErro(null);
    const receita = decimalDigitado(rbt12);
    if (receita === null) {
      setValidacao('Informe a receita bruta dos 12 meses (RBT12) em reais.');
      return;
    }
    const folhaTexto = folha12.trim();
    const folha = folhaTexto === '' ? null : decimalDigitado(folhaTexto);
    if (folha === null && folhaTexto !== '') {
      setValidacao('Folha: informe um valor em reais ou deixe em branco.');
      return;
    }
    const valido = salvarSimplesApuracaoSchema.safeParse({ rbt12: receita, folha12: folha });
    if (!valido.success) {
      setValidacao(valido.error.issues[0]?.message ?? 'Confira os valores');
      return;
    }
    try {
      await salvar.mutateAsync({ competencia: mes, corpo: valido.data });
      setSalvoEm(new Date());
    } catch (e) {
      setErro(e);
    }
  }

  const { vigente, itens } = lista;

  return (
    <section aria-label="Simples Nacional: receita e folha de 12 meses" className="flex flex-col gap-4 rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-title font-semibold">Simples Nacional — receita e folha de 12 meses</h2>
        <p className="mt-1 text-small text-ink-secondary">
          Receita bruta dos 12 meses anteriores ao mês e, para o Fator R, a folha de salários + pró-labore + encargos dos mesmos 12 meses. Vale para a alíquota efetiva
          calculada das tabelas de preço. Pode digitar com vírgula ou ponto nos centavos.
        </p>
      </div>

      {vigente ? (
        <p className="text-small text-ink-body" data-testid="simples-vigente">
          Vale hoje: {rotuloMes(vigente.competencia)} (RBT12 {formatarDinheiro(vigente.rbt12)}).
        </p>
      ) : (
        <p role="status" className="rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
          Nenhuma apuração cadastrada. A efetiva calculada do Simples não é gerada até informar a receita de 12 meses.
        </p>
      )}

      <form
        aria-label="Apuração do Simples"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void gravar();
        }}
        className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] items-end gap-4"
      >
        <div>
          <Label htmlFor="simples-apuracao-mes" className={ROTULO}>Mês</Label>
          <input id="simples-apuracao-mes" type="month" className={CAMPO} value={mes} onChange={(e) => escolherMes(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="simples-apuracao-rbt12" className={ROTULO}>Receita bruta dos últimos 12 meses (RBT12)</Label>
          <Input id="simples-apuracao-rbt12" inputMode="decimal" className="font-mono" disabled={!editavel} value={rbt12} onChange={(e) => setRbt12(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="simples-apuracao-folha12" className={ROTULO}>Folha dos últimos 12 meses (para o Fator R)</Label>
          <Input id="simples-apuracao-folha12" inputMode="decimal" className="font-mono" disabled={!editavel} value={folha12} onChange={(e) => setFolha12(e.target.value)} />
        </div>
        {editavel && (
          <div className="flex items-center gap-3">
            <button type="submit" disabled={salvar.isPending} className="h-9 rounded-md bg-primary px-4 text-small font-semibold text-primary-foreground disabled:opacity-50">
              Salvar
            </button>
            {salvoEm && (
              <span role="status" className="text-caption text-ink-muted">
                Salvo às {formatarHora(salvoEm)}
              </span>
            )}
          </div>
        )}
      </form>
      {validacao && (
        <p role="alert" className="text-caption text-danger-ink">
          {validacao}
        </p>
      )}
      {erro !== null && <ErroFormacao erro={erro} />}

      <div className="overflow-x-auto">
        {itens.length === 0 ? (
          <p className="text-small text-ink-muted">Ainda não há meses salvos.</p>
        ) : (
          <table className="w-full min-w-[520px] border-collapse text-small" aria-label="Meses salvos do Simples">
            <thead>
              <tr className="border-b border-border text-left text-caption text-ink-muted">
                <th scope="col" className="px-2 py-2 font-medium">Mês</th>
                <th scope="col" className="px-2 py-2 font-medium">RBT12</th>
                <th scope="col" className="px-2 py-2 font-medium">Folha</th>
                <th scope="col" className="px-2 py-2 font-medium">Fator R</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => (
                <tr key={i.competencia} className="border-b border-border">
                  <td className="px-2 py-2">{rotuloMes(i.competencia)}</td>
                  <td className="px-2 py-2 tabular">{formatarDinheiro(i.rbt12)}</td>
                  <td className="px-2 py-2 tabular">{i.folha12 === null ? '—' : formatarDinheiro(i.folha12)}</td>
                  <td className="px-2 py-2 tabular">{formatarFracaoPercentual(i.fatorR)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
